<template>
  <div v-if="criteria" id="backtester-basic-options-tab">
    <helpers-section-sub-header class="mt-6 mb-2"> Signal Evaluation Window: </helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      After a new 13F filing is processed, each qualifying stock becomes a potential buy candidate
      for this many days. If the portfolio has no room or the stock doesn't meet entry criteria on
      day one, it stays "active" and will be reconsidered each day until the window expires.
      Setting a longer window (e.g. 45–60 days) gives more opportunities to enter; shorter windows
      (e.g. 5–10 days) keep the signal fresh and close to the filing date.
    </p>
    <v-row>
      <v-col v-if="!appcriteria.general.screener" cols="12">
        <v-text-field
          v-model="criteria.number_of_days_to_evaluate"
          label="Days to keep signal active after filing"
          suffix="days"
          variant="outlined"
          density="compact"
          hint="Maximum: 100 days. Starts counting after trading delay."
          persistent-hint
          style="max-width: 300px"
        />
      </v-col>
    </v-row>
    <!-- Number of Holdings -->
    <v-row mb-6>
      <v-col cols="12">
        <helpers-section-sub-header>Number of Holdings to Consider:</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          The maximum number of stocks from the filer group's combined holdings to use as buy
          candidates each quarter. For example, setting 10 means only the top 10 holdings
          (ranked by your stock selection method below) are eligible to be bought.
        </p>
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="2">
        <v-select
          v-model="criteria.holdings"
          :items="holdingsItems"
          attach
          item-title="text"
          variant="outlined"
          density="compact"
          hide-details
        >
        </v-select>
      </v-col>
    </v-row>

    <v-row mb-6>
      <v-col cols="12">
        <helpers-section-sub-header># of Top Holdings to Skip:</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Skip the N largest holdings before counting. Useful for avoiding mega-cap names that
          almost every institution holds (e.g. Apple, Microsoft) — skip 2–3 to focus on more
          differentiated positions.
        </p>
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="2">
        <v-select
          v-model="criteria.skip_top_holdings"
          :items="['None', 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
          variant="outlined"
          hide-details
        >
        </v-select>
      </v-col>
    </v-row>

    <!-- When to evaluate the group -->
    <v-row mb-6>
      <v-col cols="12">
        <helpers-section-sub-header>When to Evaluate the Group:</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Controls when in the quarterly filing cycle stocks are evaluated as buy signals.
          <strong>"As filings come in"</strong> starts evaluating each filer's holdings the day
          their filing is released — earlier entry but based on incomplete group data.
          <strong>"When all filings arrive"</strong> waits until every filer in the group has
          filed for the quarter, giving you a complete group view before acting.
        </p>
      </v-col>
      <v-col cols="12">
        <v-radio-group v-model="criteria.group_evaluation_type_id" class="mt-0">
          <v-radio
            :value="2"
            label="Combine filers that file on the same date as filings come in"
          />
          <v-radio
            :value="1"
            label="Evaluate as a group when all filings arrive for a given quarter"
          />
        </v-radio-group>
        <template v-if="criteria.group_evaluation_type_id === 1">
          <p style="max-width: 500px">
            In case one of the filers in your group doesn't file for a quarter, specify the maximum
            number of days to wait for late filers after the filing deadline.
          </p>
          <v-text-field
            v-model="criteria.max_days_to_wait"
            type="number"
            min="0"
            variant="outlined"
            density="compact"
            label="Max days to wait"
            style="max-width: 200px"
          />
        </template>
      </v-col>

      <!-- Trading Delay -->
      <v-col cols="12" mb-2>
        <helpers-section-sub-header>Trading Delay:</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Number of days to wait after a filing is released before acting on it. A delay of
          <strong>1</strong> is the minimum realistic setting — the filing arrives after market
          close, so you can trade the next morning. Higher delays (e.g. 30 days) simulate a more
          conservative approach where you wait for price confirmation before entering. Set to
          <strong>0</strong> only to test the theoretical maximum (same-day fills, unrealistic).
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="criteria.trading_delay"
              type="number"
              suffix="days"
              variant="outlined"
              density="compact"
              hint="Days after filing(s) released before being considered for backtesting"
              persistent-hint
              class="pt-0"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- How do you want stocks selected when rebalancing? -->
    <v-row mb-6>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-4">
          How are stocks chosen?
          <v-btn
            variant="text"
            :icon="mdiInformation"
            class="mx-0 mt-0"
            @click="stockSelectionHelpDialog = true"
          />
        </helpers-section-sub-header>
        <v-container fluid pa-0>
          <v-row>
            <!-- Radio group main -->
            <v-col cols="12" sm="8" md="6" xl="4">
              <v-radio-group
                v-model="criteria.stock_selection_type_id"
                class="v-radio-group--x-small ma-0 pa-0"
                :disabled="criteria.crowded_stocks"
                hide-details
              >
                <template v-for="item in stockSelectionItems" :key="item.value">
                  <!-- Expand 'Absolute count among largest holdings' -->
                  <template v-if="item.value === 3">
                    <div
                      :class="{
                        'bg-grey-lighten-4': item.value === criteria.stock_selection_type_id
                      }"
                    >
                      <v-radio :value="item.value" color="primary">
                        <template #label>
                          <span class="text-body-2">{{ item.text }}</span>
                        </template>
                      </v-radio>
                      <div v-show="criteria.stock_selection_type_id === 3" class="pl-6 py-2">
                        <label class="text-caption text-grey"
                          >Top holdings to use from each fund</label
                        >
                        <v-select
                          v-model="criteria.top_holdings_per_filer"
                          :items="topRankingItems"
                          item-title="text"
                          class="pt-0"
                          variant="outlined"
                          density="compact"
                          style="max-width: 100px"
                        />
                      </div>
                    </div>
                  </template>

                  <!-- v-else Non-expandable radios -->
                  <v-radio
                    v-else
                    :disabled="!accessAllowed(item)"
                    :title="!accessAllowed(item) ? 'Pro Subscription Required For This Option' : ''"
                    :value="item.value"
                    :class="{
                      'bg-grey-lighten-4': item.value === criteria.stock_selection_type_id
                    }"
                    color="primary"
                  >
                    <template #label>
                      <span class="text-body-2">{{ item.text }}</span>
                    </template>
                  </v-radio>
                </template>
              </v-radio-group>
            </v-col>

            <!-- Optionally require a minimum threshold for stock selection -->
            <v-col cols="12" sm="4" xl="3">
              <h4 class="text-body-2 mb-2">
                Optionally require a minimum threshold for stock selection:
              </h4>
              <v-text-field
                v-model="criteria.minimum_funds"
                mask="###"
                variant="outlined"
                density="compact"
                :disabled="
                  criteria.stock_selection_type_id == 4 || criteria.stock_selection_type_id == 9
                "
                hint="Minimum number of filers holding stock"
                persistent-hint
              />
              <v-text-field
                v-model="criteria.minimum_percent_portfolio"
                :disabled="criteria.crowded_stocks"
                hint="Minimum % of portfolio per filer"
                persistent-hint
                variant="outlined"
                density="compact"
                suffix="%"
                @keypress="isNumber($event)"
              />

              <v-text-field
                v-model="criteria.exclude_if_qoq_decrease"
                :disabled="criteria.crowded_stocks"
                hint="Exclude stock if QoQ % decrease in shares held"
                persistent-hint
                suffix="%"
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
              <v-text-field
                v-model="criteria.only_include_if_qoq_increase"
                :disabled="criteria.crowded_stocks"
                hint="Only include stock if % QoQ increase in shares held"
                suffix="%"
                variant="outlined"
                density="compact"
                persistent-hint
                @keypress="isNumber($event)"
              />
              <v-text-field
                v-model="criteria.exclude_if_price_jump"
                :disabled="criteria.crowded_stocks"
                hint="Exclude stocks where price has increased __% from the EOQ date to the filing due date"
                suffix="%"
                variant="outlined"
                density="compact"
                persistent-hint
                @keypress="isNumber($event)"
              />

              <v-text-field
                v-model="criteria.include_if_price_jump"
                :disabled="criteria.crowded_stocks"
                hint="Only include stocks where price has increased __% from the EOQ date to the filing due date"
                suffix="%"
                variant="outlined"
                density="compact"
                persistent-hint
                @keypress="isNumber($event)"
              />

              <v-text-field
                v-model="criteria.exclude_positions_held_quarters"
                :disabled="criteria.crowded_stocks"
                hint="Exclude any manager's positions if those positions have been held for more than ___ quarters."
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
              <v-text-field
                v-model="criteria.exclude_held_less_than"
                :disabled="criteria.crowded_stocks"
                hint="Exclude positions that have been held less than ___ quarters."
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
              <v-checkbox
                v-model="criteria.overweight"
                :disabled="criteria.crowded_stocks"
                label="Restrict to a manager's overweight positions"
              />
              <v-checkbox
                v-model="criteria.use_original_filings"
                :disabled="criteria.crowded_stocks"
                label="Do not include holdings from amended filings that were not also in the original 13F filing"
              />
              <p>
                The PUT/CALL ratio fields below use the same quarter's data to calculate the ratio.
                This means to be realistic you need to add at least a 1-day delay to your model
                since the PUT/CALL ratio would not be available until the day after the 13F filing
                deadline.
              </p>
              <v-text-field
                v-model="criteria.include_put_call_ratio_above"
                :disabled="criteria.crowded_stocks"
                hint="Only include stocks with PUT/CALL ratio above __"
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
              <v-text-field
                v-model="criteria.include_put_call_ratio_below"
                :disabled="criteria.crowded_stocks"
                hint="Only include stocks with a PUT/CALL ratio below __"
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />

              <v-text-field
                v-model="criteria.losing_trades_percentage"
                :disabled="criteria.crowded_stocks"
                hint="Losing Trades Filter:  Only include stocks that on the quarter ending date were __% below the fund's estimated purchase price"
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
              <v-text-field
                v-model="criteria.transaction_impact_min"
                :disabled="criteria.crowded_stocks"
                hint="Minimum Transaction Impact %"
                persistent-hint
                variant="outlined"
                density="compact"
                @keypress="isNumber($event)"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <!-- Configure Short Signals -->
    <v-row v-if="1 == 2" mb-6>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-4">
          Configure Short Signals
        </helpers-section-sub-header>
        <v-checkbox
          v-model="criteria.short_signal_stocks_drop_out"
          label="Short signal when stock falls out of top holdings for group"
        />
      </v-col>
    </v-row>

    <!-- Stock selection help dialog -->
    <v-dialog v-model="stockSelectionHelpDialog" width="600px">
      <v-card>
        <v-card-title class="d-block primary text-white">
          <h4 class="text-h6 mb-1">Stock Selection Options</h4>
          <span class="text-body-2"
            >This section lets you choose how you want the stocks to be selected from each filer's
            holdings.</span
          >
        </v-card-title>
        <v-card-text class="pa-6">
          <dl>
            <dt>Absolute count</dt>
            <dd>
              This will count the number of filers that hold at least one share of a stock for the
              quarter being rebalanced.
            </dd>
            <dt>Dollar value</dt>
            <dd>
              Takes the top securities ordered by market value from a combined portfolio of the list
              of filers.
            </dd>
            <dt>Combined Percent of Portfolio</dt>
            <dd>
              Takes the top securities by summing each securities percent of portfolio for each
              filer.
            </dd>
            <dt>Absolute count that appear among the largest 10 holdings</dt>
            <dd>
              Will look at the top XX largest holdings from each selected filer and then picks the
              most frequently held stocks (not necessarily the ones with the largest market value)
              among all of the selected filers.
            </dd>
            <dt>Top # Holdings by Dollar Value from each funds holdings</dt>
            <dd>
              Takes the top XX holdings from each filer's portfolio into a combined portfolio.
            </dd>
            <dt>Select only new buys during the quarter</dt>
            <dd>
              Looks at only new buys each quarter. Will select the top new buys based on combined
              percent of portfolio.
            </dd>
            <dt>Combined % of Portfolio with custom weight for each individual fund</dt>
            <dd>
              Takes the top securities by summing each securities percent of portfolio for each
              filer. You have the option to over or under-weight individual funds.
            </dd>
            <dt>Combined Percent Ownership</dt>
            <dd>
              Similar to the existing rule "Combined Percent of Portfolio". But instead of adding up
              the portfolio weights, this criteria adds up the % values of all 13F filers in a group
              for a certain stock.
            </dd>
            <dt>Top # Holding(s) by Percent Ownership from each funds holdings</dt>
            <dd>
              Similar to the existing rule "Top # Holding(s) by %Dollar Value from each funds
              holding". But instead of selecting the X largest stock of each 13F filer the
              backtesting rule selects the X stocks of each filer that shows the highest % Ownership
              for each filer.
            </dd>
          </dl>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Custom weights dialog -->
    <v-dialog v-model="customWeightDialog" persistent width="700px">
      <v-card>
        <v-card-title class="d-block primary text-white">
          <h4 class="text-h6 mb-1">Custom Weight Per Fund</h4>
          Scale of 1 through 10
        </v-card-title>
        <v-card-text>
          <v-alert v-if="!selectedGroup.filers.length" :value="true" color="error" :icon="mdiAlert">
            No filers have been added yet.
          </v-alert>
          <table v-else>
            <tr v-for="filer in selectedGroup.filers" :key="filer.id">
              <td>
                <input
                  v-model="filer.weight"
                  type="number"
                  min="1"
                  max="10"
                  class="mr-4 text-center font-weight-bold"
                  style="border: solid 1px #aaa; width: 75px"
                />
              </td>
              <td>{{ filer.name }}</td>
            </tr>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="ClearWeight()"> Reset Weight </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="customWeightDialog = false"> Done </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'
// Mixins
import AccessMixin from '@/mixins/access'
import { mdiAlert, mdiInformation } from '@mdi/js'

export default {
  mixins: [AccessMixin],

  data: () => ({
    mdiAlert,
    mdiInformation,
    stockSelectionHelpDialog: false,
    customWeightDialog: false,
    holdingsItems: [
      {
        text: '1 Holding',
        value: 1,
        requires_subscription: false
      },
      {
        text: '2 Holdings',
        value: 2,
        requires_subscription: false
      },
      {
        text: '3 Holdings',
        value: 3,
        requires_subscription: false
      },
      {
        text: '4 Holdings',
        value: 4,
        requires_subscription: false
      },
      {
        text: '5 Holdings',
        value: 5,
        requires_subscription: false
      },
      {
        text: '6 Holdings',
        value: 6,
        requires_subscription: false
      },
      {
        text: '7 Holdings',
        value: 7,
        requires_subscription: false
      },
      {
        text: '8 Holdings',
        value: 8,
        requires_subscription: false
      },
      {
        text: '9 Holdings',
        value: 9,
        requires_subscription: false
      },

      {
        text: '10 Holdings',
        value: 10,
        requires_subscription: false
      },
      {
        text: '11 Holdings',
        value: 11,
        requires_subscription: false
      },
      {
        text: '12 Holdings',
        value: 12,
        requires_subscription: false
      },
      {
        text: '13 Holdings',
        value: 13,
        requires_subscription: false
      },
      {
        text: '14 Holdings',
        value: 14,
        requires_subscription: false
      },
      {
        text: '15 Holdings',
        value: 15,
        requires_subscription: false
      },
      {
        text: '20 Holdings',
        value: 20,
        requires_subscription: false
      },
      {
        text: '30 Holdings',
        value: 30,
        requires_pro_subscription: false
      },
      {
        text: '50 Holdings',
        value: 50,
        requires_pro_subscription: false
      },
      {
        text: '75 Holdings',
        value: 75,
        requires_pro_subscription: false
      },

      {
        text: '100 Holdings',
        value: 100,
        requires_pro_subscription: false
      }
    ],
    stockSelectionItems: [
      {
        text: 'Absolute Count',
        value: 1,
        tooltip: 'Counts the number of filers holding at least one share of stock'
      },
      {
        text: 'Dollar Value',
        value: 2,
        tooltip: 'Sums the total market values of each stock and returns the top picks'
      },
      {
        text: 'Combined % of Portfolio',
        value: 6,
        tooltip: 'Sums a given stocks percent of portfolio for the funds selected.'
      },
      {
        text: 'Absolute count among largest holdings',
        value: 3,
        tooltip:
          "Counts the number of filers holding at least one share of stock, but limits the stock selection to a fund's top XX number of holdings."
      },
      {
        text: "Top Holding(s) by $ from each fund's holdings",
        value: 4,
        tooltip: 'Takes each individual funds top holdings'
      },
      {
        text: 'Select only new buys during quarter',
        value: 5,
        tooltip:
          'Takes the top stocks ordered by summing the percent of portfolio, limits stocks to new buys during the quarter only.'
      },

      {
        text: 'Combined Percent Ownership',
        value: 8,
        tooltip: 'Sums the percent ownership of each fund selected.',
        requires_pro_subscription: false
      },
      {
        text: "Top Holding(s) by % Ownership from each fund's holdings",
        value: 9,
        tooltip: 'Takes each individual funds top % owned holdings',
        requires_pro_subscription: false
      },
      {
        text: 'Treat new positions only as buy signals',
        value: 10,
        tooltip: 'Only new positions in the top holdings for the group are used as a buy signal',
        requires_pro_subscription: false
      }
    ],
    topRankingItems: [
      { text: '5', value: '1' },
      { text: '10', value: '2' },
      { text: '20', value: '3' },
      { text: '30', value: '4' },
      { text: '40', value: '5' },
      { text: '50', value: '6' }
    ],
    weightSelectionItems: [
      {
        text: 'Manager Weight Stocks',
        value: 1,
        tooltip: 'Short instructions here'
      },
      {
        text: 'Equally Weight Stocks',
        value: 2,
        tooltip: 'Short instructions here'
      }
    ],
    radioGroup: 1
  }),

  computed: {
    storeInit() {
      // Watch for store initialization
      return useUserStore().isLoggedIn
    },
    ...mapStoreState({
      quarters: state => state.ww.quarters.unshift({ id: 0, description: 'All Available Dates' }),
      groups: state => state.user.groups
    }),
    ...mapWritableState(useAppStore, ['whaleTraderCriteria']),
    criteria() {
      return this.whaleTraderCriteria.signals.f13_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    selectedGroup() {
      return this.whaleTraderCriteria.signals.f13_signal.filer_group
    }
    /*
    ...mapFields({
      appCriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.f13_signal',
      selectedGroup: 'app.whaleTraderCriteria.signals.f13_signal.filer_group'

    })*/
  },

  watch: {
    customWeightDialog(val) {
      if (val) {
        this.selectedGroup.filers.forEach(filer => {
          if (!filer.hasOwnProperty('weight')) filer['weight'] = 1
        })
      }
    },
    storeInit(val) {
      this.holdingsItems.forEach(holding => {
        holding['disabled'] = !this.accessAllowed(holding)
      })
    }
  },

  created() {
    this.holdingsItems.forEach(holding => {
      holding['disabled'] = !this.accessAllowed(holding)
    })
  },

  methods: {
    ClearWeight() {
      this.selectedGroup.filers.forEach(filer => {
        filer['weight'] = 1
      })
    },
    isNumber(evt) {
      evt = evt || window.event
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46 && charCode !== 45) {
        evt.preventDefault()
      } else {
        return true
      }
    }
  }
}
</script>
